import React from "react";

import type { IAggregatedEvent } from "~/types/event";

import * as Styled from "./SearchOption.styles";

/**
 * Atlas Search highlight snippet as returned on aggregated-event DTOs (both the
 * dropdown `AggregatedEventResponseDto` and the results-list
 * `AggregatedEventListItemDto` expose this identical shape).
 */
export type ISearchHighlight = {
  path?: string;
  texts?: {
    value?: string;
    type?: "hit" | "text";
  }[];
};

export const TITLE_HIGHLIGHT_PATHS = [
  "displayName",
  "searchableTitles",
] as const;

export const MARKET_QUESTION_HIGHLIGHT_PATHS = ["marketDocs.question"] as const;

const highlightTermsInText = (
  text: string,
  terms: string[]
): React.ReactNode => {
  const uniqueTerms = [...new Set(terms.filter((t) => t.trim().length > 0))];
  if (uniqueTerms.length === 0) return text;

  // Build a regex that matches any of the hit terms
  const escaped = uniqueTerms.map((t) =>
    t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const regex = new RegExp(`(${escaped.join("|")})`, "gi");

  const parts = text.split(regex);
  return parts.map((part, i) =>
    uniqueTerms.some((t) => t.toLowerCase() === part.toLowerCase()) ? (
      <Styled.HighlightedText key={i}>{part}</Styled.HighlightedText>
    ) : (
      part
    )
  );
};

/**
 * Highlights the search query inside a single field's `text`, always rendering
 * the FULL text. Prefers BE highlight fragments for `path` (they know which
 * fragments matched, typo/synonym-aware); falls back to client-side substring
 * matching against `query` when no BE highlight is available for that path.
 *
 * Shared by the search dropdown option and the search results list so both
 * surfaces highlight identically.
 */
export const getHighlightedFieldText = (
  text: string,
  {
    query,
    highlights,
    paths,
  }: {
    query?: string;
    highlights?: ISearchHighlight[];
    paths: readonly string[];
  }
): React.ReactNode => {
  if (!text) return text;

  if (highlights?.length) {
    const relevant = highlights.filter((h) => h.path && paths.includes(h.path));
    if (relevant.length > 0) {
      // Collect all highlighted ("hit") fragments from BE
      const hitTerms = relevant
        .flatMap((h) => h.texts ?? [])
        .filter((t) => t.type === "hit")
        .map((t) => t.value)
        .filter((value): value is string => value != null);
      if (hitTerms.length > 0) {
        return highlightTermsInText(text, hitTerms);
      }
    }
  }

  // Client-side fallback
  return getHighlightedText(text, query ?? "");
};

export const getSearchTitle = (
  option: IAggregatedEvent,
  inputValue: string
): React.ReactNode => {
  const isMarket = option.resultType === "market";
  const displayText = isMarket
    ? (option.marketQuestion ?? option.title)
    : option.title;

  return getHighlightedFieldText(displayText, {
    query: inputValue,
    highlights: option.highlights,
    paths: isMarket ? MARKET_QUESTION_HIGHLIGHT_PATHS : TITLE_HIGHLIGHT_PATHS,
  });
};

export const getHighlightedText = (text: string, queryString: string) => {
  const queryStringTrimmed = queryString.trim();
  if (!queryStringTrimmed) return text;

  const startIndex = text
    .toLowerCase()
    .indexOf(queryStringTrimmed.toLowerCase());
  if (startIndex < 0) return text;

  const endIndex = startIndex + queryStringTrimmed.length;

  return (
    <>
      {text.slice(0, startIndex)}
      <Styled.HighlightedText>
        {text.slice(startIndex, endIndex)}
      </Styled.HighlightedText>
      {text.slice(endIndex)}
    </>
  );
};
